"use client";

// The expanded media viewer — the menu gesture, applied to media. The page
// fades away entirely; the active media sits center-left with the next one
// peeking in from the right; drag / swipe / dots move between them; the whole
// ground is tinted with the ACTIVE media's own primary color and retints as
// you move. Click any empty area (or press Escape) to close.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { thumbSrc } from "./MediaThumb";
import type { CompanyMedia } from "../../lib/company/types";
import { lockScroll } from "../../lib/os/scrollLock";

// average a same-origin image down to one color (the media's "tone") when the
// post didn't declare one.
function computeTone(src: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = c.height = 24;
        const ctx = c.getContext("2d");
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, 24, 24);
        const d = ctx.getImageData(0, 0, 24, 24).data;
        let r = 0, g = 0, b = 0, n = 0;
        for (let i = 0; i < d.length; i += 4) {
          if (d[i + 3] < 40) continue;
          r += d[i]; g += d[i + 1]; b += d[i + 2]; n++;
        }
        if (!n) return resolve(null);
        const hex = (v: number) => Math.round(v / n).toString(16).padStart(2, "0");
        resolve(`#${hex(r)}${hex(g)}${hex(b)}`);
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function inkFor(tone: string): string {
  const m = tone.match(/^#([0-9a-f]{6})$/i);
  if (!m) return "#fff";
  const int = parseInt(m[1], 16);
  const r = (int >> 16) & 255, g = (int >> 8) & 255, b = int & 255;
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return lum > 0.62 ? "rgba(20,16,22,0.92)" : "#fff";
}

function VideoItem({ m }: { m: CompanyMedia }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [paused, setPaused] = useState(false);
  return (
    <>
      <video ref={ref} src={m.src} poster={m.poster} autoPlay muted loop playsInline />
      <button
        type="button"
        className="co-modal__toggle"
        data-paused={paused || undefined}
        aria-label={paused ? "play" : "pause"}
        onClick={(e) => {
          e.stopPropagation();
          const v = ref.current;
          if (!v) return;
          if (v.paused) { v.play().catch(() => {}); setPaused(false); }
          else { v.pause(); setPaused(true); }
        }}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
          {paused ? <path d="M8 5.5v13l11-6.5z" /> : <><rect x="7" y="5" width="3.4" height="14" rx="1.2" /><rect x="13.6" y="5" width="3.4" height="14" rx="1.2" /></>}
        </svg>
      </button>
    </>
  );
}

export default function MediaModal({
  media,
  initialIndex,
  crumb,
  onClose,
}: {
  media: CompanyMedia[];
  initialIndex: number;
  /** the breadcrumb trail shown inside the viewer, e.g. ["company","blog","launch with love"] */
  crumb: string[];
  onClose: () => void;
}) {
  const [active, setActive] = useState(initialIndex);
  const [tones, setTones] = useState<(string | null)[]>(() => media.map((m) => m.tone ?? null));
  const railRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ x0: number; s0: number; moved: boolean } | null>(null);

  // resolve missing tones from the pixels themselves
  useEffect(() => {
    media.forEach((m, i) => {
      if (m.tone) return;
      const probe = thumbSrc(m);
      if (!probe) return;
      computeTone(probe).then((t) => {
        if (t) setTones((cur) => (cur[i] === t ? cur : cur.map((v, j) => (j === i ? t : v))));
      });
    });
  }, [media]);

  const tone = tones[active] ?? "#1c1c1e";
  const ink = useMemo(() => inkFor(tone), [tone]);

  // the page holds still while the viewer is up (the counted lock, held for
  // the viewer's whole life: `onClose` may be a new function each render)
  useEffect(() => lockScroll("media"), []);

  // escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // land on the tapped media, instantly
  useEffect(() => {
    const el = itemRefs.current[initialIndex];
    const rail = railRef.current;
    if (el && rail) rail.scrollLeft = el.offsetLeft - parseFloat(getComputedStyle(rail).paddingLeft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // active follows the scroll (snap or drag alike)
  const onScroll = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    const pad = parseFloat(getComputedStyle(rail).paddingLeft);
    let best = 0, bestD = Infinity;
    itemRefs.current.forEach((el, i) => {
      if (!el) return;
      const d = Math.abs(el.offsetLeft - pad - rail.scrollLeft);
      if (d < bestD) { bestD = d; best = i; }
    });
    setActive((cur) => (cur === best ? cur : best));
  }, []);

  const goTo = (i: number) => {
    const el = itemRefs.current[i];
    const rail = railRef.current;
    if (!el || !rail) return;
    rail.scrollTo({ left: el.offsetLeft - parseFloat(getComputedStyle(rail).paddingLeft), behavior: "smooth" });
  };

  // desktop drag-to-swipe on the rail
  const onDown = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    const rail = railRef.current;
    if (!rail) return;
    drag.current = { x0: e.clientX, s0: rail.scrollLeft, moved: false };
  };
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    const rail = railRef.current;
    if (!d || !rail) return;
    const dx = e.clientX - d.x0;
    if (!d.moved && Math.abs(dx) > 5) { d.moved = true; setDragging(true); }
    if (d.moved) rail.scrollLeft = d.s0 - dx;
  };
  const onUp = () => {
    const d = drag.current;
    drag.current = null;
    if (d?.moved) {
      setDragging(false);
      goTo(activeRef.current); // settle onto the nearest snap point
      setTimeout(() => { suppressClose.current = false; }, 0);
      suppressClose.current = true;
    }
  };
  const activeRef = useRef(active);
  activeRef.current = active;
  const suppressClose = useRef(false);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="co-modal"
      style={{ ["--co-tone" as string]: tone, ["--co-modal-ink" as string]: ink } as React.CSSProperties}
      onClick={() => { if (!suppressClose.current) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="media"
      data-gesture-root=""
    >
      <div className="co-modal__crumb" style={{ color: ink }}>
        <span className="co-crumb__seg">innernet</span>
        {crumb.map((c, i) => (
          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: "0.55rem" }}>
            <span className="co-crumb__sep" aria-hidden>/</span>
            <span className="co-crumb__seg" data-here={i === crumb.length - 1 || undefined}>{c}</span>
          </span>
        ))}
      </div>
      <div
        ref={railRef}
        className="co-modal__rail"
        data-dragging={dragging || undefined}
        onScroll={onScroll}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
      >
        {media.map((m, i) => (
          <div
            key={i}
            ref={(el) => { itemRefs.current[i] = el; }}
            className="co-modal__item"
            data-yt={m.type === "youtube" || undefined}
            style={{ animationDelay: `${i * 60}ms` }}
            onClick={(e) => e.stopPropagation()}
          >
            {m.type === "youtube" ? (
              <iframe
                className="co-modal__yt"
                style={{ width: "100%", aspectRatio: "16 / 9", height: "auto", maxHeight: "calc(100svh - 220px)", display: "block", border: 0 }}
                src={`https://www.youtube-nocookie.com/embed/${m.videoId}?autoplay=1&rel=0&modestbranding=1`}
                title={m.alt ?? "video"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : m.type === "video" ? <VideoItem m={m} /> : (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={m.src} alt={m.alt ?? ""} draggable={false} />
            )}
          </div>
        ))}
      </div>
      {(media[active]?.context || media[active]?.alt) && (
        <div className="co-modal__ctx" style={{ color: ink }} onClick={(e) => e.stopPropagation()}>
          {media[active]?.context || media[active]?.alt}
        </div>
      )}
      {media.length > 1 && (
        <div className="co-modal__dots" onClick={(e) => e.stopPropagation()}>
          {media.map((_, i) => (
            <button
              key={i}
              type="button"
              className="co-modal__dot"
              data-on={i === active || undefined}
              aria-label={`media ${i + 1}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
}
